import React, { useEffect, useMemo } from 'react'
import { t } from 'ttag'
import { useForm, Controller } from 'react-hook-form'
import { Box, Chip, FormGroup, Link, ListItem, ListItemText, ListItemIcon, TextField } from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import YumButton from '../../components/YumButton/YumButton'
import YumSwitch from '../../components/YumSwitch/YumSwitch'
import { Card } from '../../components/Card/Card'
import NotificationsWrapper from '../../components/Notification/NotificationsWrapper'
import YumSmallSelect from '../../components/YumSmallSelect/YumSmallSelect'
import YumHeader from '../../components/YumHeader/YumHeader'
import { GetCompanyInfo$Response, UserCompanyDto } from '../../services/api.types'
import { localizeCountries, LocalizedCountry } from '../../components/YumCountrySelect/YumCountrySelect'
import { ReactComponent as CountryInactiveIcon } from '../../assets/v3/icons/country-inactive.svg'
import { ReactComponent as RequireTeamSelectionInactiveIcon } from '../../assets/v3/icons/require-team-selection-inactive.svg'
import { darkBlue } from '../../styles/material-ui-theme-v2'

// --- Helpers to parse/serialize country JSON ---
function parseCountries(raw: string | null | undefined): string[] {
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

// --- Form data shape ---
interface CountriesFormData {
  countries: string[]
  countryRequired: boolean
}

// --- Props ---
export interface ICountriesAdminProps {
  managedCompanies: UserCompanyDto[]
  companyInfo: GetCompanyInfo$Response | null
  loading: boolean
  saving: boolean
  lastUpdated: number | null
  selectedCompanyId: string | null
}

export interface ICountriesAdminDispatchProps {
  getCompanyInfo: (payload: { companyId: string }) => void
  updateCompanyInfo: (payload: any) => void
  setCompanyAdminSelectedCompanyId: (payload: string) => void
}

type CountriesAdminProps = ICountriesAdminProps & ICountriesAdminDispatchProps

export default function CountriesAdmin(props: CountriesAdminProps) {
  const {
    managedCompanies,
    companyInfo,
    loading,
    saving,
    lastUpdated,
    selectedCompanyId,
    getCompanyInfo,
    updateCompanyInfo,
    setCompanyAdminSelectedCompanyId,
  } = props

  const companyId = selectedCompanyId || (managedCompanies.length > 0 ? managedCompanies[0].companyId : null)

  const { control, handleSubmit, reset, watch, setValue } = useForm<CountriesFormData>({
    defaultValues: {
      countries: [],
      countryRequired: false,
    },
  })

  // All countries for the multi-select dropdown
  const allCountries = useMemo(() => localizeCountries([]), [])

  // Set default company on first mount
  useEffect(() => {
    if (companyId && !selectedCompanyId) {
      setCompanyAdminSelectedCompanyId(companyId)
    }
  }, [])

  // Load company info whenever selected company changes
  useEffect(() => {
    if (companyId) {
      getCompanyInfo({ companyId })
    }
  }, [companyId])

  // Populate form when company info loads
  useEffect(() => {
    if (companyInfo) {
      reset({
        countries: parseCountries(companyInfo.countries),
        countryRequired: companyInfo.countryRequired || false,
      })
    } else {
      reset({
        countries: [],
        countryRequired: false,
      })
    }
  }, [companyInfo, lastUpdated])

  // Handle company selection change
  const handleCompanyChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newCompanyId = event.target.value as string
    setCompanyAdminSelectedCompanyId(newCompanyId)
    getCompanyInfo({ companyId: newCompanyId })
  }

  // Handle form submit — send full companyInfo with updated countries fields
  const onSubmit = (data: CountriesFormData) => {
    if (!companyId || !companyInfo) return
    updateCompanyInfo({
      companyId,
      name: companyInfo.name || '',
      brandLogoUrl: companyInfo.brandLogoUrl || '',
      hideStatisticsView: companyInfo.hideStatisticsView || false,
      hideRankingView: companyInfo.hideRankingView || false,
      userGoalsEnabled: companyInfo.userGoalsEnabled || false,
      autoJoinEnabled: companyInfo.autoJoinEnabled || false,
      reportRequired: companyInfo.reportRequired || false,
      reportRequiredForUsers: companyInfo.reportRequiredForUsers || false,
      passwordMinLength: companyInfo.passwordMinLength || null,
      customPolicyUrl: companyInfo.customPolicyUrl || '',
      countries: JSON.stringify(data.countries),
      countryRequired: data.countryRequired,
    })
  }

  return (
    <Card>
      <NotificationsWrapper />

      <YumHeader>
        <h1>
          {t`Countries`}
          {!loading && <span style={{ opacity: 0.5, whiteSpace: 'nowrap' }}> ({watch('countries').length})</span>}
        </h1>
        {managedCompanies.length > 1 && (
          <YumSmallSelect variant="outlined" value={companyId} onChange={handleCompanyChange}>
            {managedCompanies.map((userCompany: UserCompanyDto, keyIndex: number) => (
              <option key={keyIndex} value={userCompany.companyId}>
                {userCompany.companyName}
              </option>
            ))}
          </YumSmallSelect>
        )}
      </YumHeader>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Box style={{ position: 'relative', marginBottom: 0 }}>
          <CountryInactiveIcon
            style={{
              width: 24,
              height: 24,
              //opacity: 0.5,
              position: 'absolute',
              left: 12,
              top: 16,
              zIndex: 1,
              pointerEvents: 'none',
            }}
          />
          <Controller
            name="countries"
            control={control}
            render={({ field }) => (
              <Autocomplete
                multiple
                openOnFocus
                options={allCountries}
                value={allCountries.filter((c) => field.value.includes(c.code))}
                onChange={(_event, newValue) => {
                  field.onChange(newValue.map((c: LocalizedCountry) => c.code))
                }}
                getOptionLabel={(option) => option.country}
                getOptionSelected={(option, value) => option.code === value.code}
                renderOption={(option) => <>{option.countryWithFlag}</>}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      key={option.code}
                      label={option.countryWithFlag}
                      size="small"
                      style={{ backgroundColor: '#fff' }}
                      {...getTagProps({ index })}
                    />
                  ))
                }
                clearText={t`Clear`}
                closeText={t`Close`}
                openText={t`Open`}
                noOptionsText={t`No options`}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t`Countries`}
                    variant="filled"
                    margin="dense"
                    fullWidth
                    InputLabelProps={{
                      ...params.InputLabelProps,
                      shrink: true,
                      style: { marginLeft: 33 },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: true,
                      style: { paddingLeft: 40 },
                    }}
                  />
                )}
              />
            )}
          />
        </Box>

        <Box display="flex" justifyContent="space-between" marginBottom={3}>
          <Link
            component="button"
            type="button"
            variant="body2"
            underline="none"
            style={{ cursor: 'pointer', borderBottom: '1px solid transparent' }}
            onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.currentTarget.style.borderBottomColor = 'currentColor'
            }}
            onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.currentTarget.style.borderBottomColor = 'transparent'
            }}
            onClick={() =>
              setValue(
                'countries',
                allCountries.map((c) => c.code),
              )
            }
          >
            {t`Add all countries`}
          </Link>
          <Link
            component="button"
            type="button"
            variant="body2"
            underline="none"
            style={{ cursor: 'pointer', borderBottom: '1px solid transparent' }}
            onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.currentTarget.style.borderBottomColor = 'currentColor'
            }}
            onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.currentTarget.style.borderBottomColor = 'transparent'
            }}
            onClick={() => setValue('countries', [])}
          >
            {t`Clear`}
          </Link>
        </Box>

        <FormGroup>
          <Controller
            name="countryRequired"
            control={control}
            render={({ field: { value, ref, ...field } }) => (
              <ListItem role={undefined} dense button onClick={() => setValue('countryRequired', !value)}>
                <ListItemIcon>
                  <RequireTeamSelectionInactiveIcon />
                </ListItemIcon>
                <ListItemText primary={t`Require country selection`} />
                <ListItemIcon style={{ minWidth: 'auto' }}>
                  <YumSwitch
                    {...field}
                    checked={!!value}
                    onChange={() => setValue('countryRequired', !value)}
                    inputRef={ref}
                  />
                </ListItemIcon>
              </ListItem>
            )}
          />
        </FormGroup>

        {/* --- Save Button --- */}
        <Box style={{ marginTop: 24 }}>
          <YumButton type="submit" showLoader={true} disabled={saving || !companyId}>
            {t`Save changes`}
          </YumButton>
        </Box>
      </form>
    </Card>
  )
}
